import React, { useState } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Divider,
  Paper,
  ClickAwayListener,
} from '@mui/material';
import {
  FiHome,
  FiUsers,
  FiTarget,
  FiHeart,
  FiMail,
  FiTag,
  FiBell,
  FiSettings,
  FiMenu,
  FiLogOut,
  FiUser,
  FiChevronRight,
} from 'react-icons/fi';
import { FaRupeeSign } from 'react-icons/fa';
import { FaHandsHoldingChild } from 'react-icons/fa6';
import { BiDonateHeart } from 'react-icons/bi';
import { FaHandHoldingHeart } from 'react-icons/fa';
import { FaWhatsapp } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { colors } from '../theme/modernTheme';
import { PiHandHeart } from 'react-icons/pi';

const drawerWidth = 210; // Compact sidebar width

const getMenuItems = (MODULE_PERMISSIONS, canUseEmail, canUseWhatsApp) => {
  // Determine campaign text based on permissions
  let campaignText = 'Manage Campaigns';
  if (canUseEmail && !canUseWhatsApp) {
    campaignText = ' Email Campaigns';
  } else if (!canUseEmail && canUseWhatsApp) {
    campaignText = 'WhatsApp Campaigns';
  }

  return [
    {
      text: 'Dashboard',
      icon: <FiHome size={20} />,
      path: '/',
      roles: ['ADMIN', 'STAFF'],
    },
    {
      text: 'Contacts',
      icon: <FiUsers size={20} />,
      path: '/contacts',
      roles: ['ADMIN', 'STAFF'],
      modulePermission: MODULE_PERMISSIONS.CONTACTS,
    },
    {
      text: 'Campaigns',
      icon: <FiTarget size={20} />,
      path: '/campaigns',
      roles: ['ADMIN', 'STAFF'],
      // Show if user has either email or whatsapp marketing permission
      modulePermissions: [
        MODULE_PERMISSIONS.EMAIL_MARKETING,
        MODULE_PERMISSIONS.WHATSAPP_MARKETING,
      ],
      subItems: [
        {
          text: campaignText,
          icon: <FiTarget size={18} />,
          path: '/campaigns',
          roles: ['ADMIN', 'STAFF'],
          modulePermissions: [
            MODULE_PERMISSIONS.EMAIL_MARKETING,
            MODULE_PERMISSIONS.WHATSAPP_MARKETING,
          ],
        },
        { divider: true },
        {
          text: 'Email Templates',
          icon: <FiMail size={18} />,
          path: '/email-templates',
          roles: ['ADMIN', 'STAFF'],
          modulePermission: MODULE_PERMISSIONS.EMAIL_TEMPLATE,
        },
        {
          text: 'WhatsApp Templates',
          icon: <FaWhatsapp size={18} />,
          path: '/whatsapp-templates',
          roles: ['ADMIN', 'STAFF'],
          modulePermission: MODULE_PERMISSIONS.WHATSAPP_TEMPLATE,
        },
      ],
    },
    {
      text: 'Donation',
      icon: <FiHeart size={20} />,
      roles: ['ADMIN', 'STAFF'],
      // Show if user has any donation permission
      modulePermissions: [
        MODULE_PERMISSIONS.DONATION_PLEDGE,
        MODULE_PERMISSIONS.DONATION_REMINDER,
        MODULE_PERMISSIONS.DONATION_TRANSACTION,
      ],
      subItems: [
        {
          text: 'Pledges',
          icon: <BiDonateHeart size={18} />,
          path: '/donations',
          roles: ['ADMIN', 'STAFF'],
          modulePermission: MODULE_PERMISSIONS.DONATION_PLEDGE,
        },
        {
          text: 'Reminders',
          icon: <FiBell size={18} />,
          path: '/reminders',
          roles: ['ADMIN', 'STAFF'],
          modulePermission: MODULE_PERMISSIONS.DONATION_REMINDER,
        },
        {
          text: 'Transactions',
          icon: <FaRupeeSign size={18} />,
          path: '/transactions',
          roles: ['ADMIN', 'STAFF'],
          modulePermission: MODULE_PERMISSIONS.DONATION_TRANSACTION,
        },
      ],
    },
    {
      text: 'Settings',
      icon: <FiSettings size={20} />,
      roles: ['ADMIN', 'STAFF'],
      subItems: [
        {
          text: 'Tags',
          icon: <FiTag size={18} />,
          path: '/tags',
          roles: ['ADMIN', 'STAFF'],
          modulePermission: MODULE_PERMISSIONS.TAGS,
        },
        {
          text: 'Donation Schemes',
          icon: <PiHandHeart size={18} />,
          path: '/donation-schemas',
          roles: ['ADMIN', 'STAFF'],
          modulePermission: MODULE_PERMISSIONS.DONATION_SCHEME,
        },
        {
          text: 'Trusts',
          icon: <FaHandsHoldingChild size={18} />,
          path: '/trust-masters',
          roles: ['ADMIN', 'STAFF'],
          modulePermission: MODULE_PERMISSIONS.TRUST_MASTER,
        },
      ],
    },
    {
      text: 'Users',
      icon: <FiUser size={20} />,
      path: '/users',
      roles: ['ADMIN'], // Only ADMIN can see Users - staff never sees this
    },
  ];
};

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [submenuAnchor, setSubmenuAnchor] = useState(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const { user, logout, hasModulePermission, hasAnyModulePermission, MODULE_PERMISSIONS } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Check channel permissions
  const canUseEmail = hasModulePermission(MODULE_PERMISSIONS.EMAIL_MARKETING);
  const canUseWhatsApp = hasModulePermission(MODULE_PERMISSIONS.WHATSAPP_MARKETING);

  const menuItems = getMenuItems(MODULE_PERMISSIONS, canUseEmail, canUseWhatsApp);

  // Helper function to check if user has permission for a menu item
  const hasMenuPermission = (item) => {
    // Check role first
    if (!item.roles.includes(user?.role)) {
      return false;
    }

    // ADMIN always has access
    if (user?.role === 'ADMIN') {
      return true;
    }

    // Check single module permission
    if (item.modulePermission) {
      return hasModulePermission(item.modulePermission);
    }

    // Check multiple module permissions (any of them)
    if (item.modulePermissions && item.modulePermissions.length > 0) {
      return hasAnyModulePermission(item.modulePermissions);
    }

    // No module permission required
    return true;
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleProfileMenuOpen = (event) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleViewProfile = () => {
    navigate('/profile');
    handleProfileMenuClose();
  };

  const handleSubmenuOpen = (event, item) => {
    event.stopPropagation();
    // If clicking the same menu item that's already open, close it
    if (activeSubmenu?.text === item.text) {
      handleSubmenuClose();
    } else {
      setSubmenuAnchor(event.currentTarget);
      setActiveSubmenu(item);
    }
  };

  const handleSubmenuClose = () => {
    setSubmenuAnchor(null);
    setActiveSubmenu(null);
  };

  const handleClickAway = (event) => {
    // Don't close if clicking on a sidebar menu item
    if (event.target.closest('[data-sidebar-menu]')) {
      return;
    }
    handleSubmenuClose();
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.background.sidebar,
      }}
    >
      {/* Brand Section */}
      <Link to="/" style={{ textDecoration: 'none' }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            component="img"
            src="/logo.png"
            alt="TyagDMS Logo"
            sx={{
              width: 40,
              height: 40,
              borderRadius: 5, // Slightly rounded square for modern look
              objectFit: 'cover',
            }}
          />
          <Typography
            variant="h6"
            sx={{
              mt: '5px',
              fontWeight: 700,
              color: colors.text.primary,
              letterSpacing: '-0.02em',
            }}
          >
            TyagDMS
          </Typography>
        </Box>
      </Link>

      {/* Navigation Menu */}
      <Box sx={{ flex: 1 }} data-sidebar-menu>
        <List disablePadding>
          {menuItems
            .filter((item) => hasMenuPermission(item))
            .map((item) => {
              const hasSubItems = item.subItems && item.subItems.length > 0;

              // Filter sub-items based on permissions
              const filteredSubItems = hasSubItems
                ? item.subItems.filter((subItem) => subItem.divider || hasMenuPermission(subItem))
                : [];

              // Skip parent if no visible sub-items (except dividers)
              const visibleSubItems = filteredSubItems.filter((s) => !s.divider);
              if (hasSubItems && visibleSubItems.length === 0) {
                return null;
              }

              // Check if this item or any of its sub-items is active
              let isActive = false;
              if (hasSubItems) {
                // For items with sub-items, check if any sub-item is active
                isActive = filteredSubItems.some(
                  (subItem) => subItem.path && location.pathname.startsWith(subItem.path),
                );
              } else if (item.path) {
                // For items without sub-items, check direct path match
                isActive =
                  item.path === '/'
                    ? location.pathname === '/'
                    : location.pathname.startsWith(item.path);
              }

              return (
                <ListItem key={item.text} disablePadding sx={{ m: 0, p: 0 }}>
                  <ListItemButton
                    onClick={(e) => {
                      if (hasSubItems && visibleSubItems.length > 0) {
                        handleSubmenuOpen(e, {
                          ...item,
                          subItems: filteredSubItems,
                        });
                      } else if (item.path) {
                        handleSubmenuClose(); // Close any open submenu
                        navigate(item.path);
                        if (isMobile) setMobileOpen(false);
                      }
                    }}
                    selected={isActive && !hasSubItems}
                    sx={{
                      borderRadius: 0,
                      mx: 0,
                      my: 0.5,
                      color: isActive ? colors.primary.main : colors.text.secondary,
                      backgroundColor:
                        isActive && !hasSubItems ? colors.action.selected : 'transparent',
                      '&:hover': {
                        backgroundColor:
                          isActive && !hasSubItems ? colors.action.selected : colors.action.hover,
                        color: isActive ? colors.primary.main : colors.text.primary,
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 32,
                        color: isActive ? 'inherit' : colors.text.primary,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? colors.chart.primary : colors.text.primary,
                      }}
                    />
                    {hasSubItems && visibleSubItems.length > 0 && (
                      <Box
                        sx={{
                          color: colors.text.secondary,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <FiChevronRight size={16} />
                      </Box>
                    )}
                  </ListItemButton>
                </ListItem>
              );
            })
            .filter(Boolean)}
        </List>
      </Box>

      {/* User Profile Summary in Sidebar (Optional, keeps sidebar persistent and useful) */}
      <Box sx={{ p: 1, borderTop: `1px solid ${colors.border.main}` }}>
        <Box
          onClick={handleProfileMenuOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: '4px',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: colors.primary.light,
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {getInitials(user?.name)}
          </Avatar>
          <Box sx={{ overflow: 'hidden' }}>
            <Typography
              variant="subtitle2"
              noWrap
              sx={{ fontWeight: 600, color: colors.text.primary }}
            >
              {user?.name}
            </Typography>
            <Typography variant="caption" noWrap sx={{ color: colors.text.secondary }}>
              {user?.role}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: colors.background.default,
      }}
    >
      {/* Mobile Menu Button */}
      <IconButton
        color="inherit"
        edge="start"
        onClick={handleDrawerToggle}
        sx={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1200,
          display: { md: 'none' },
          backgroundColor: colors.background.paper,
          boxShadow: 1,
          '&:hover': {
            backgroundColor: colors.background.paper,
          },
        }}
      >
        <FiMenu />
      </IconButton>

      {/* Sidebar */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 'none',
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop Drawer - Persistent */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: `1px solid ${colors.border.main}`,
              backgroundColor: colors.background.sidebar,
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Floating Submenu */}
      {Boolean(submenuAnchor) && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            elevation={0}
            sx={{
              position: 'fixed',
              left: submenuAnchor ? submenuAnchor.getBoundingClientRect().right + 8 : 0,
              top: submenuAnchor ? submenuAnchor.getBoundingClientRect().top : 0,
              minWidth: 200,
              backgroundColor: colors.background.sidebar,
              borderRadius: '4px',
              border: `1px solid ${colors.border.main}`,
              py: 1,
              zIndex: 1300,
              overflow: 'hidden',
            }}
          >
            <List disablePadding sx={{ backgroundColor: 'transparent' }}>
              {activeSubmenu?.subItems
                ?.filter((subItem) => !subItem.divider && hasMenuPermission(subItem))
                .map((subItem, index, filteredArray) => {
                  const isSubActive = location.pathname.startsWith(subItem.path);

                  // Check if there's a divider before this item in the original array
                  const originalIndex = activeSubmenu.subItems.indexOf(subItem);
                  const hasDividerBefore =
                    originalIndex > 0 && activeSubmenu.subItems[originalIndex - 1]?.divider;

                  return (
                    <React.Fragment key={subItem.text}>
                      {hasDividerBefore && <Divider sx={{ my: 1 }} />}
                      <ListItem disablePadding sx={{ m: 0, p: 0 }}>
                        <ListItemButton
                          onClick={() => {
                            navigate(subItem.path);
                            handleSubmenuClose();
                            if (isMobile) setMobileOpen(false);
                          }}
                          sx={{
                            borderRadius: 0,
                            mx: 0,
                            my: 0.5,
                            color: isSubActive ? colors.primary.main : colors.text.secondary,
                            backgroundColor: isSubActive ? colors.action.selected : 'transparent',
                            '&:hover': {
                              backgroundColor: isSubActive
                                ? colors.action.selected
                                : colors.action.hover,
                              color: isSubActive ? colors.primary.main : colors.text.primary,
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 32,
                              color: isSubActive ? 'inherit' : colors.text.primary,
                            }}
                          >
                            {subItem.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={subItem.text}
                            primaryTypographyProps={{
                              fontSize: '0.9rem',
                              fontWeight: isSubActive ? 600 : 500,
                              color: isSubActive ? colors.chart.primary : colors.text.primary,
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    </React.Fragment>
                  );
                })}
            </List>
          </Paper>
        </ClickAwayListener>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          p: { xs: 1, md: 3 },
          mt: { xs: 8, md: 0 }, // Add margin top on mobile for the menu button
        }}
      >
        {/* Profile Menu Popover */}
        <Menu
          anchorEl={profileMenuAnchor}
          open={Boolean(profileMenuAnchor)}
          onClose={handleProfileMenuClose}
          onClick={handleProfileMenuClose}
          autoFocus={false}
          transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
              mt: -1,
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                bottom: -10,
                left: 20,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
        >
          <MenuItem
            onClick={handleViewProfile}
            sx={{
              height: 50,
              width: 200,
              '&:hover': { backgroundColor: colors.background.default },
            }}
          >
            <ListItemIcon>
              <FiUser />
            </ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem
            onClick={handleLogout}
            sx={{
              color: 'error.main',
              height: 50,
              width: 200,
              '&:hover': { backgroundColor: colors.background.default },
            }}
          >
            <ListItemIcon>
              <FiLogOut color={colors.error} />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>

        <Outlet />
      </Box>
    </Box>
  );
}
